import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import { superAdminNavItems } from '../../constants/navigation';

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'password', label: 'Password' },
  { id: 'health', label: 'System Health' },
];

const SuperAdminSettingsPage = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <DashboardLayout title="Settings" navItems={superAdminNavItems} userRole="super-admin" userName={currentUser?.email || 'Admin'}>
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Settings</h2>

      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-4">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>{tab.label}</button>
          ))}
        </div>
      </div>

      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'password' && <PasswordTab />}
      {activeTab === 'health' && <HealthTab />}
    </DashboardLayout>
  );
};

// ============================================================================
// PROFILE TAB
// ============================================================================
const ProfileTab = () => {
  const [, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '' });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await apiRequest('/api/super-admin/profile', 'GET');
        setProfile(data.profile);
        setForm({ full_name: data.profile?.full_name || '', email: data.profile?.email || '' });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiRequest('/api/super-admin/profile', 'PUT', form);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h3>

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input type="text" value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" required />
        </div>
        <button type="submit" disabled={saving}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// PASSWORD TAB
// ============================================================================
const PasswordTab = () => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getStrength = (password) => {
    if (!password) return { level: 0, label: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    if (score <= 2) return { level: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { level: 2, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 4) return { level: 3, label: 'Good', color: 'bg-blue-500' };
    return { level: 4, label: 'Strong', color: 'bg-green-500' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (form.new_password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    try {
      setSaving(true);
      await apiRequest('/api/super-admin/password', 'PUT', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setSuccess('Password changed successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const strength = getStrength(form.new_password);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
          <input type="password" value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
          <input type="password" value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" required />
          {form.new_password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={`h-1 flex-1 rounded-full ${i <= strength.level ? strength.color : 'bg-gray-200'}`} />
                ))}
              </div>
              <p className="text-xs text-gray-500">{strength.label}</p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
          <input type="password" value={form.confirm_password}
            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" required />
          {form.confirm_password && form.new_password !== form.confirm_password && (
            <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
          )}
        </div>
        <button type="submit"
          disabled={saving || !form.current_password || !form.new_password || form.new_password !== form.confirm_password}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">
          {saving ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// SYSTEM HEALTH TAB
// ============================================================================
const HealthTab = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const data = await apiRequest('/api/super-admin/health', 'GET');
        setHealth(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchHealth();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <Alert type="error" message={error} />;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <span className="font-medium text-gray-700">Status</span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            health?.status === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>{health?.status || 'Unknown'}</span>
        </div>
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <span className="font-medium text-gray-700">Database</span>
          <span className={`text-sm font-medium ${
            health?.database === 'connected' ? 'text-green-600' : 'text-red-600'
          }`}>{health?.database || 'Unknown'}</span>
        </div>

        {health?.counts && (
          <div className="grid grid-cols-2 gap-3 mt-4">
            <InfoCard label="Schools" value={health.counts.schools} />
            <InfoCard label="Students" value={health.counts.students} />
            <InfoCard label="Teachers" value={health.counts.teachers} />
            <InfoCard label="Activation Codes" value={health.counts.activation_codes} />
          </div>
        )}

        {health?.timestamp && (
          <p className="text-xs text-gray-500 mt-4">
            Last checked: {new Date(health.timestamp).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="border border-gray-200 rounded-lg p-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-lg font-bold text-gray-900">{value}</p>
  </div>
);

// ============================================================================
// SHARED COMPONENTS
// ============================================================================
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const Alert = ({ type, message, onDismiss }) => {
  const styles = type === 'error'
    ? 'bg-red-50 border-red-200 text-red-700'
    : 'bg-green-50 border-green-200 text-green-700';
  return (
    <div className={`border rounded-lg p-3 mb-4 flex justify-between items-center ${styles}`}>
      <span className="text-sm">{message}</span>
      {onDismiss && <button onClick={onDismiss} className="text-sm opacity-60 hover:opacity-100 ml-2">&times;</button>}
    </div>
  );
};

export default SuperAdminSettingsPage;
